import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, 
  Calendar, 
  Euro, 
  CheckCircle2, 
  XCircle,
  Gift,
  Loader2,
  ArrowRight,
  Shield,
  Sun,
  Moon
} from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const ALPHABIT_LOGO = "/logo-alphabit.png";

const PublicSubscriptionPage = () => {
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [plan, setPlan] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [error, setError] = useState(null);
  
  // Success/Cancel page states
  const [isSuccessPage, setIsSuccessPage] = useState(false);
  const [isCancelPage, setIsCancelPage] = useState(false);
  const [successData, setSuccessData] = useState(null);
  
  // Form
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    const subId = searchParams.get('sub_id');
    const path = window.location.pathname;
    
    if (path.includes('/success') && subId) {
      setIsSuccessPage(true);
      handleSuccess(subId);
    } else if (path.includes('/cancel')) {
      setIsCancelPage(true);
      setLoading(false);
    } else {
      fetchPlan();
    }
  }, [planId, searchParams]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/subscribe/${planId}`);
      setPlan(response.data.plan);
      setMerchant(response.data.merchant);
    } catch (error) {
      console.error('Error fetching plan:', error);
      setError(error.response?.data?.detail || 'Piano non trovato');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = async (subId) => {
    try {
      const response = await axios.get(`${API_URL}/api/public/subscribe/${planId}/success?sub_id=${subId}`);
      setSuccessData(response.data);
    } catch (error) {
      console.error('Error verifying subscription:', error);
      setSuccessData({ status: 'pending', message: 'Abbonamento in elaborazione...' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Email non valida');
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await axios.post(`${API_URL}/api/public/subscribe/${planId}/create`, {
        subscriber_email: email,
        subscriber_name: name || null
      });
      
      // Redirect to PayPal
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        toast.error('Errore: URL approvazione non trovato');
        setSubscribing(false);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Errore creazione abbonamento');
      setSubscribing(false);
    }
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(amount || 0);
  };

  const getIntervalLabel = (interval) => {
    const labels = {
      weekly: 'settimana',
      monthly: 'mese',
      quarterly: 'trimestre',
      yearly: 'anno'
    };
    return labels[interval] || interval;
  };

  // Get branding colors
  const primaryColor = merchant?.branding?.primary_color || '#7c3aed';
  const brandName = merchant?.branding?.display_name || merchant?.business_name || 'Merchant';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Success Page
  if (isSuccessPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-xl border-slate-200">
          <CardContent className="p-8 text-center">
            {successData?.status === 'success' || successData?.subscription_status === 'active' ? (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Abbonamento Attivato!</h1>
                <p className="text-slate-500 mb-6">
                  Grazie per esserti abbonato. Riceverai una conferma via email.
                </p>
                <div className="space-y-3">
                  <Button 
                    className="w-full text-white"
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => navigate(`/subscribe/${planId}`)}
                  >
                    Torna al Sito
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-slate-300 text-slate-600 hover:bg-slate-50"
                    onClick={() => window.close()}
                  >
                    Chiudi Finestra
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-10 h-10 text-yellow-600 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Elaborazione...</h1>
                <p className="text-slate-500 mb-6">
                  {successData?.message || 'Il tuo abbonamento è in fase di attivazione.'}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cancel Page
  if (isCancelPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-xl border-slate-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Abbonamento Annullato</h1>
            <p className="text-slate-500 mb-6">
              Hai annullato la procedura di abbonamento. Puoi riprovare quando vuoi.
            </p>
            <Button 
              variant="outline"
              className="w-full border-slate-300 text-slate-600 hover:bg-slate-50"
              onClick={() => navigate(`/subscribe/${planId}`)}
            >
              Torna Indietro
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error Page
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-xl border-slate-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Errore</h1>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Subscription Page
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-white shadow-xl border-slate-200">
        <CardHeader className="text-center pb-2">
          {/* AlphaBit Logo */}
          <div className="flex justify-center mb-4">
            <img 
              src={ALPHABIT_LOGO} 
              alt="Alpha Bit" 
              className="h-12 object-contain"
            />
          </div>
          
          {/* Merchant info */}
          <p className="text-slate-500 text-sm">Offerto da {brandName}</p>
          <CardTitle className="text-2xl text-slate-800 mt-2">{plan?.name}</CardTitle>
          {plan?.description && (
            <CardDescription className="text-slate-500 mt-2">
              {plan.description}
            </CardDescription>
          )}
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Price */}
          <div className="text-center py-4 border-y border-slate-200">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl font-bold text-slate-800">
                {formatCurrency(plan?.amount, plan?.currency)}
              </span>
              <span className="text-slate-500">/ {getIntervalLabel(plan?.interval)}</span>
            </div>
            {plan?.trial_days > 0 && (
              <Badge className="mt-3 bg-green-100 text-green-700 border-green-200">
                <Gift className="w-3 h-3 mr-1" />
                {plan.trial_days} giorni di prova gratuita
              </Badge>
            )}
          </div>

          {/* Features */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-slate-600">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span>Pagamento sicuro con Alpha Bit</span>
            </div>
            <div className="flex items-center gap-3 text-slate-600">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <span>Cancellazione facile in qualsiasi momento</span>
            </div>
            <div className="flex items-center gap-3 text-slate-600">
              <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span>Addebito automatico {plan?.interval_label || getIntervalLabel(plan?.interval)}</span>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-slate-700">Email *</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tua@email.com"
                className="bg-white border-slate-300 text-slate-800"
                data-testid="subscriber-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">Nome (opzionale)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Il tuo nome"
                className="bg-white border-slate-300 text-slate-800"
              />
            </div>
          </div>

          {/* Subscribe Button */}
          <Button
            className="w-full h-12 text-lg font-semibold text-white hover:opacity-90"
            style={{ backgroundColor: primaryColor || '#7c3aed' }}
            onClick={handleSubscribe}
            disabled={subscribing}
            data-testid="subscribe-btn"
          >
            {subscribing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Elaborazione pagamento...
              </>
            ) : (
              <>
                Abbonati Ora
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>

          {/* AlphaBit Security Badge */}
          <div className="flex flex-col items-center gap-2 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Shield className="w-4 h-4 text-green-600" />
              <span>Pagamento sicuro tramite Alpha Bit</span>
            </div>
            <p className="text-xs text-slate-400">Powered by Alpha Bit</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicSubscriptionPage;
